import { get, set, del, update } from 'idb-keyval'
import { api, ErroRede } from './api.js'

const CHAVE_FILA = 'fila-abastecimentos'
const CHAVE_DESCARTADOS = 'fila-descartados'
const CHAVE_SESSAO = 'sessao:ultima'
const ouvintes = new Set()

/**
 * Cache do que o formulário de lançamento precisa para funcionar sem rede:
 * veículos, pontos e o último odômetro. Sem isto, "offline-first" seria só uma
 * fila — a tela nem conseguiria se montar no estacionamento sem sinal.
 *
 * É por usuário: num aparelho compartilhado, quem entra depois não pode ver, sem
 * rede, os veículos e pontos de quem saiu.
 */
export const guardarCache = (usuarioId, chave, valor) => set(`cache:${usuarioId}:${chave}`, valor)
export const lerCache = (usuarioId, chave) => get(`cache:${usuarioId}:${chave}`)

/**
 * Última sessão confirmada pelo servidor. Abrir o app sem rede consultava a sessão,
 * falhava e caía no login — e o formulário offline ficava inalcançável justo no
 * estacionamento sem sinal. Aqui fica só o que a tela mostra (nome, papel); quem
 * autoriza continua sendo o cookie httpOnly, que nenhum JavaScript lê.
 */
export const guardarSessao = (usuario) => set(CHAVE_SESSAO, usuario)
export const lerSessao = () => get(CHAVE_SESSAO)

/** Ao sair: some a sessão guardada e o cache dela. A fila fica — ver `sair()`. */
export async function esquecerUsuario(usuarioId) {
  await del(CHAVE_SESSAO)
  await del(`cache:${usuarioId}:formulario`)
}

const lerFila = async () => (await get(CHAVE_FILA)) ?? []
const lerDescartados = async () => (await get(CHAVE_DESCARTADOS)) ?? []

// Toda escrita na fila é ler-e-gravar numa transação só (`update`). Ler, esperar e
// gravar depois apagava o que tivesse entrado no meio — um lançamento feito
// enquanto a sincronização rodava sumia sem aviso.
async function alterar(chave, alteracao) {
  await update(chave, (atual) => alteracao(atual ?? []))
  const [fila, descartados] = await Promise.all([lerFila(), lerDescartados()])
  for (const ouvinte of ouvintes) ouvinte({ fila, descartados })
}

/** Avisa a interface quando a fila ou a lista de descartados muda. */
export function observarFila(ouvinte) {
  ouvintes.add(ouvinte)
  Promise.all([lerFila(), lerDescartados()]).then(([fila, descartados]) => ouvinte({ fila, descartados }))
  return () => ouvintes.delete(ouvinte)
}

/**
 * Item é de quem o lançou. Itens sem dono são de antes desta versão do app: ficam
 * com quem estiver logado, que era o comportamento de quando foram criados.
 */
export const eDoUsuario = (item, usuarioId) => !item.usuario_id || item.usuario_id === usuarioId

/**
 * Enfileira um lançamento feito sem conexão.
 *
 * A `idempotency_key` é gerada aqui, no cliente, e viaja com o registro: se a
 * sincronização mandar o mesmo item duas vezes (retry, aba duplicada, app reaberto),
 * o servidor devolve o registro original em vez de criar outro — SPEC §7.
 */
export async function enfileirar(lancamento, usuarioId) {
  const item = {
    ...lancamento,
    idempotency_key: lancamento.idempotency_key ?? crypto.randomUUID(),
    usuario_id: usuarioId,
    enfileirado_em: new Date().toISOString(),
  }
  await alterar(CHAVE_FILA, (fila) => [...fila, item])
  return item
}

/** Quantos lançamentos do usuário ainda esperam envio. */
export const pendentesDo = async (usuarioId) =>
  (await lerFila()).filter((item) => eDoUsuario(item, usuarioId)).length

/** Dispensa o aviso de um descartado (pela chave) depois que a pessoa o viu. */
export const dispensarDescartado = (chave) =>
  alterar(CHAVE_DESCARTADOS, (lista) => lista.filter((d) => d.idempotency_key !== chave))

// Falhas que não dizem nada sobre o lançamento em si: o item fica e tenta de novo.
// Sessão vencida era a pior delas — o 401 descartava o lançamento, e a sessão
// vence sozinha (30 dias) ou cai quando a senha é trocada em outro aparelho.
const motivoParaParar = (erro) => {
  if (erro instanceof ErroRede) return 'rede'
  if (erro.status === 401 || erro.status === 403) return 'sessao'
  if (erro.status === 408 || erro.status === 429 || erro.status >= 500) return 'servidor'
  return null
}

let emAndamento = null

/**
 * Envia a fila do usuário, na ordem em que foi lançada.
 *
 * Diante de uma falha passageira (rede, sessão, servidor) o envio **para**: seguir
 * adiante mandaria os lançamentos fora de ordem, e a validação de odômetro recusaria
 * os que ficaram para trás. Só uma recusa definitiva do servidor (odômetro
 * inválido, veículo apagado) tira o item da fila — e ele vai para a lista de
 * descartados, que a interface mostra até a pessoa dispensar.
 *
 * Uma chamada por vez: a de quem chegar enquanto outra roda recebe o mesmo resultado.
 */
export function sincronizar(usuarioId) {
  if (!usuarioId) return Promise.resolve({ enviados: 0, descartados: 0, parada: null })
  emAndamento ??= enviarFila(usuarioId).finally(() => { emAndamento = null })
  return emAndamento
}

async function enviarFila(usuarioId) {
  const minhas = (await lerFila()).filter((item) => eDoUsuario(item, usuarioId))
  const resolvidos = new Set()
  const descartados = []
  let parada = null

  for (const item of minhas) {
    const { enfileirado_em: _, usuario_id: __, ...corpo } = item
    try {
      await api.post('/abastecimentos', corpo)
      resolvidos.add(item.idempotency_key)
    } catch (erro) {
      parada = motivoParaParar(erro)
      if (parada) break
      descartados.push({ ...item, usuario_id: usuarioId, motivo: erro.message, descartado_em: new Date().toISOString() })
      resolvidos.add(item.idempotency_key)
    }
  }

  if (descartados.length > 0) await alterar(CHAVE_DESCARTADOS, (lista) => [...lista, ...descartados])
  // Tira só o que foi resolvido; o que entrou durante o envio continua lá.
  if (resolvidos.size > 0) {
    await alterar(CHAVE_FILA, (fila) => fila.filter((item) => !resolvidos.has(item.idempotency_key)))
  }

  return { enviados: resolvidos.size - descartados.length, descartados: descartados.length, parada }
}

/**
 * Sincroniza ao entrar, ao voltar a conexão e quando a página volta a ficar
 * visível (o celular que passou a noite no bolso). Começa quando a sessão é
 * conhecida — antes disso não há de quem enviar.
 */
export function iniciarSincronizacaoAutomatica(usuarioId, { aoPerderSessao } = {}) {
  const tentar = async () => {
    if (!navigator.onLine) return
    const r = await sincronizar(usuarioId).catch(() => null)
    if (r?.parada === 'sessao') aoPerderSessao?.()
  }
  const aoMudarVisibilidade = () => { if (document.visibilityState === 'visible') tentar() }

  window.addEventListener('online', tentar)
  document.addEventListener('visibilitychange', aoMudarVisibilidade)
  tentar()
  return () => {
    window.removeEventListener('online', tentar)
    document.removeEventListener('visibilitychange', aoMudarVisibilidade)
  }
}
